/*#
# doc-browser

`createDocBrowser()` renders a documentation corpus as a browsable, searchable testbed with
live examples, like the [tosijs-ui documentation site](https://ui.tosijs.net). It's like
Storybook but much simpler to set up and maintain.

For a whole documentation site (static, pre-rendered pages, SEO, ePub, a dev server), use
[`tosijs-ui/site`](/adopting-the-doc-system/) instead; it builds on this component. Reach for
`createDocBrowser` directly to embed a doc browser in an app of your own.

## Quick Start

### 1. Extract the documentation

Run once at build time (it reads files, so it runs under bun or node, not in the browser):

```typescript
import { extractDocs } from 'tosijs-ui/site'

extractDocs({
  paths: ['src', 'README.md'],
  output: 'docs.json',
})
```

This collects every `.md` file and every `/*#` comment block in your sources. The format,
metadata and which code fences run are described under
[Doc format](/doc-site-system/#doc-format).

### 2. Create your doc browser

```typescript
import { createDocBrowser } from 'tosijs-ui/doc-browser'
import * as mylib from './my-library.js'
import docs from './docs.json'

const browser = createDocBrowser({
  docs,
  context: { mylib },
  projectName: 'My Project',
  projectLinks: {
    github: 'https://github.com/user/project',
    npm: 'https://www.npmjs.com/package/project',
  },
})

document.body.append(browser)
```

### 3. Add live examples to your docs

Consecutive `html`, `css`, `js` (or `ts`, `tjs`) and `test` code fences become one live,
editable example. `context` supplies the modules those examples import:

    /*#
    # My Component

    This component does amazing things!

    ```js
    import { myComponent } from 'mylib'
    preview.append(myComponent({ value: 'Hello!' }))
    ```
    ```css
    my-component {
      color: blue;
    }
    ```
    *‎/

## createDocBrowser Options

```typescript
interface DocBrowserOptions {
  docs: Doc[] // the extracted corpus
  context?: Record<string, any> // modules live examples can import
  projectName?: string // display name
  projectLinks?: ProjectLinks // links in the header
  logo?: string // header logo
  navSize?: number // nav width (default: 200)
  minSize?: number // width below which the nav goes compact (default: 600)
  routing?: 'query' | 'path' | 'memory' // see below (default: 'query')
  initialRoute?: string // memory routing: the first page
  onRouteChange?: (slug: string) => void // memory routing: page changes
  searchShortcut?: string // opens the search; '' for none (default: 'mod+k')
  navbarLinks?: LinkItem[] // icon links in the header bar
  contentElement?: HTMLElement // adopt this element as the content area
}
```

`routing` picks how pages map to URLs. `'query'` links pages as `?filename`. `'path'` uses
clean `/slug/` URLs, as the static site does. `'memory'` never touches the page's URL or
history, for a browser embedded in another page: drive it with `initialRoute`,
`onRouteChange` and the element's `navigate(slug)` method.

The search opens on Cmd-K (Ctrl-K off a Mac) anywhere on the page. A host that owns that key,
or embeds more than one browser, passes `searchShortcut: ''` (or another key in
`<tosi-palette>`'s `shortcut` form).
*/
/*{ "parent": "Documentation System", "order": 50, "description": "createDocBrowser() renders a documentation corpus as a searchable browser with live, self-testing examples: a lighter alternative to Storybook." }*/

import {
  elements,
  vars,
  varDefault,
  bindings,
  getListItem,
  tosi,
  StyleSheet,
  XinStyleSheet,
} from 'tosijs'
import {
  buildSlugMap,
  pathForSlug,
  filenameForPath,
  homeDoc,
  wikilinkTargets,
} from './doc-system/routing.js'
import { buildNavTree, NavNode } from './doc-system/nav-tree.js'
import { navSectionStyleSpec } from './doc-system/nav-styles.js'
import {
  renderDocMarkdown,
  type AccessibilityMode,
} from './doc-system/render.js'
import { pageTitle } from './doc-system/doc-title.js'
import {
  unsettledExamples,
  tallyExamples,
  closePage,
} from './doc-system/test-completion.js'
import {
  LiveExample,
  testManager,
  pageTestCount,
  enableTests,
  disableTests,
} from './live-example.js'
import { TestResults } from './live-example/test-harness.js'
import { highlightBlocks } from './doc-system/highlight.js'
import { examplePolicy } from './live-example/insert-examples.js'
import { collectCodeTokens } from './doc-system/code-fences.js'
import { isLiveFence, type ExamplePolicy } from './doc-system/example-policy.js'
import { tosiSidenav, TosiSidenav } from './side-nav.js'
import { icons } from './icons.js'
import { tosiPalette, type PaletteResult } from './palette.js'
import { buildDocIndex, searchDocs } from './doc-system/doc-search.js'
import { tosiLocalized, localize, i18n } from './localize.js'
import { popMenu } from './menu.js'
import { codeEditor, CodeEditor } from './code-editor.js'
import { tosiDiff } from './diff.js'
import { LAN_REFUSAL_ADVICE } from './live-example/source-access.js'
import { servedForDevelopment } from './live-example/dev-served.js'

/*
Does this doc contain a REAL ` ```test ` block, as opposed to mentioning one?

This was `doc.text.includes('```test')` — a substring match anywhere in the document — so a
page that merely WRITES ABOUT the test tier qualified. `doc-site-system.md` documents this very
runner, so it was the one page in the corpus qualifying on prose alone: 17 counted, 16 with
actual tests.

Not merely wasteful. That page was rendered in a background iframe and its `js`/`ts` examples
were EXECUTED, so a failure in an example on a page with no tests surfaced through the doc-test
tier — and only sometimes, because whether the failure arrived before the page's deadline
depended on how quickly the TypeScript compiler happened to load. That is what produced a
standalone run reporting "62 passed" against a build the full suite failed on.

A fence is only a fence at the start of a line, which is the rule the doc extractor already
applies to `/*#`. `[ \t]*` allows the indented fences that appear inside list items.
*/
/*
…and a line-anchored regex was not the rule either (#2874). A ` ```test ` line SHOWN inside a
longer or `:static` block matched it, so a page that documents the test tier was counted as a
page with tests, ran in a background iframe, reported nothing, and was counted as tested. The
lane said "19 of 19 pages" over results for 18. Code blocks now come from marked, the parser
the page is rendered with, and liveness from `isLiveFence`, under the site's policy.
*/
export const hasTestBlock = (
  text: string | undefined,
  policy: ExamplePolicy = 'auto'
): boolean =>
  typeof text === 'string' &&
  // Cheap reject first: lexing every doc in a large corpus is not free.
  text.includes('test') &&
  collectCodeTokens(text).some(
    (t) => t.lang === 'test' && isLiveFence('test', t.mode, policy)
  )

// Types for global test results
export interface PageTestResults {
  passed: boolean
  tests: TestResults['tests']
  totalPassed: number
  totalFailed: number
}

export interface DocTestResults {
  passed: number
  failed: number
  pages: Record<string, PageTestResults>
  /**
   * How many pages the runner INTENDED to run, and how many reported back.
   *
   * `passed + failed > 0` only proves the runner started. It cannot tell a full corpus from
   * a corpus that silently lost half its pages — and a page-selection defect that ran 16 of
   * 17 pages is exactly the false green fixed in 1.14.0, so this is a live failure mode
   * rather than a hypothetical one. A consumer gate should assert
   * `pagesTested === pagesWithTests && pagesWithTests > 0`.
   *
   * Framed by tosijs-platform in #142: "we didn't look" and "we looked and it's fine" must
   * not produce the same output. Reporting a total is how a skipped run stays comfortable.
   */
  pagesWithTests: number
  pagesTested: number
}

declare global {
  interface Window {
    __docTestResults?: Promise<DocTestResults>
  }
}

const { div, span, a, header, button, template, img, ul, li } = elements

/*
Test colors, and the text color that goes ON each — because contrast is a PAIR.

The widget used one hardcoded `color: white` and swapped only the background, so the
amber running state rendered white-on-#fa0 at **1.9:1** and the green pass state at
**3.1:1**, both well under WCAG AA's 4.5:1. (Found by `hj map`, which reports a
`contrast` ratio per node — invisible to the eye until measured.)

Amber is a genuinely light color: darkening it far enough for white text turns it brown
(63% brightness) and stops reading as "in progress". So it keeps its hue and takes dark
text at 11:1, which is the conventional warning-badge treatment. Green only needed a
nudge (81% brightness) to carry white.
*/
const testColor = {
  pass: varDefault.testColorPass('#008a00'),
  fail: varDefault.testColorFail('#c00'),
  running: varDefault.testColorRunning('#fa0'),
}
const testTextColor = {
  pass: varDefault.testTextColorPass('#fff'),
  fail: varDefault.testTextColorFail('#fff'),
  running: varDefault.testTextColorRunning('#2b1a00'),
}

// Test indicator styles - widget inherits button styles from base stylesheet
const testIndicatorStyleSpec: XinStyleSheet = {
  '@keyframes test-pulse': {
    '0%, 100%': { opacity: '1' },
    '50%': { opacity: '0.7' },
  },
  '@keyframes test-appear': {
    from: { opacity: '0', transform: 'scale(0.8)' },
    to: { opacity: '1', transform: 'scale(1)' },
  },
  '@keyframes test-fade': {
    '0%, 20%': { opacity: '1', transform: 'scale(1)' },
    '70%': { opacity: '1', transform: 'scale(1.1)' },
    '100%': { opacity: '0', transform: 'scale(0.9)', pointerEvents: 'none' },
  },
  /*
  A pass in the header settles into a muted, monochrome badge rather than fading out. Faded,
  it left an empty rectangle in the bar (the widget keeps its box), and the result was gone
  for anyone who looked a moment late. Muted, it reads as "nothing to see" and stays a button.
  All of it is a filter over the pass colour, never a new colour. Grey first, then faded: fading
  while still green put a washed-out green on the header's colour.
  */
  '@keyframes test-settle': {
    '0%, 40%': { filter: 'grayscale(0) opacity(1)' },
    '55%': { filter: 'grayscale(1) opacity(1)' },
    '100%': { filter: 'grayscale(1) opacity(0.55)' },
  },

  // Hide when tests disabled
  'body:not(.tests-enabled) .doc-link::after, body:not(.tests-enabled) .test-widget':
    {
      display: 'none !important',
    },
  // ...EXCEPT a far-end status (a failed build). That is not a test result, so the
  // "show test indicators" preference must not suppress it — otherwise the one
  // person who turned tests off is the one who never learns their build is broken.
  'body .test-widget.-dev-status': { display: 'flex !important' },

  // Nav link dot indicators
  '.doc-link.-test-passed::after, .doc-link.-test-failed::after': {
    content: "''",
    width: vars.fontSize50,
    height: vars.fontSize50,
    borderRadius: '50%',
    marginLeft: vars.spacing50,
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  '.doc-link.-test-passed::after': { background: testColor.pass },
  '.doc-link.-test-failed::after': {
    background: testColor.fail,
    animation: 'test-pulse 2s ease-in-out infinite',
  },

  /*
  The widget sits in the header bar, not over the page (GitHub #233).

  It used to be `position: fixed` at the bottom left, on top of whatever the page drew there:
  a full-page component's list ran under it and a click on its last row landed on the widget
  instead. Only a page with no header (`{"header": false}`) still floats it, because there is
  nowhere else to put it. Bottom-LEFT, because the haltija dev widget takes bottom-right.
  */
  '.test-widget': {
    _testBg: testColor.running,
    _testFg: testTextColor.running,
    background: vars.testBg,
    // Paired with the background, not fixed to white — see testTextColor above.
    color: vars.testFg,
    gap: vars.spacing50,
  },
  '.test-widget.-floating': {
    position: 'fixed',
    bottom: vars.spacing,
    left: vars.spacing,
    zIndex: '1000',
  },
  '.test-widget[hidden]': { display: 'none' },
  '.test-widget.-running': {
    _testBg: testColor.running,
    _testFg: testTextColor.running,
    animation:
      'test-appear 0.3s ease-out, test-pulse 2s ease-in-out 0.3s infinite',
  },
  '.test-widget.-passed': {
    _testBg: testColor.pass,
    _testFg: testTextColor.pass,
    animation: 'test-settle 3s ease-out forwards',
  },
  // Floating over the page (no header), it still gets out of the way: there is no gap to leave.
  '.test-widget.-floating.-passed': {
    animation: 'test-fade 3s ease-out forwards',
  },
  '@media (prefers-reduced-motion: reduce)': {
    '.test-widget.-passed': {
      animation: 'none',
      filter: 'grayscale(1) opacity(0.55)',
    },
    '.test-widget.-floating.-passed': { opacity: '0', pointerEvents: 'none' },
  },
  '.test-widget.-failed': {
    _testBg: testColor.fail,
    _testFg: testTextColor.fail,
    animation: 'test-pulse 2s ease-in-out infinite',
  },

  // Count badge
  '.test-widget .count': {
    background: 'white',
    color: vars.testBg,
    borderRadius: '50%',
    width: vars.lineHeight,
    height: vars.lineHeight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
  },

  // Deep-linked example (e.g. arriving at /slug/#example-2): brief highlight pulse
  '@keyframes example-target-pulse': {
    from: { boxShadow: `0 0 0 3px ${varDefault.accent('#0064d2')}` },
    to: { boxShadow: '0 0 0 3px transparent' },
  },
  '.example-target': {
    animation: 'example-target-pulse 1.5s ease-out',
    borderRadius: vars.roundedRadius,
  },
}

export interface Doc {
  text: string
  title: string
  filename: string
  path: string
  pin?: string
  hidden?: boolean
  /** this doc is the site root instead of README (the site's `home` option) */
  home?: boolean
  /** `false` drops the site header on this page (see the site `Doc.header`) */
  header?: boolean
  /** `'full-width'` drops the reading measure; `'full-screen'` also puts the nav away. */
  layout?: 'full-width' | 'full-screen'
  testStatus?: 'passed' | 'failed' | 'pending'
  // Build-time transpiled JS for this doc's `tjs` examples ([source, {dialect, js}]
  // entries), attached to docs.json by generate-site so client-side navigation runs
  // examples without the tjs transpiler. See self-contained-examples-plan.md.
  bakes?: Array<[string, { dialect: string; js: string }]>
}

export interface ProjectLinks {
  github?: string
  npm?: string
  discord?: string
  blog?: string
  tosijs?: string
  bundle?: string
  cdn?: string
  [key: string]: string | undefined
}

/** A configurable link for the header bar or the overflow menu. */
export interface LinkItem {
  href: string
  label: string
  /** optional icon name (from `icons`); falls back to the text label if unknown */
  icon?: string
}

/**
 * How the doc browser maps docs to URLs.
 * - 'query' (default, legacy): single-page app, links are `?filename`.
 * - 'path': clean per-page URLs (`/slug/`), for the static pre-rendered site
 *   driven by <tosi-doc-system>. Requires a real page to exist at each path.
 * - 'memory': self-contained — navigation never touches window.history/location
 *   and the instance ignores the page URL. For an embedded/nested browser (a
 *   live demo, a floating help panel). Drive it via `initialRoute` +
 *   `onRouteChange`, and the returned element's `.navigate(slug)` method.
 */
export type DocRoutingMode = 'query' | 'path' | 'memory'

export interface DocBrowserOptions {
  docs: Doc[]
  context?: Record<string, any>
  projectName?: string
  projectLinks?: ProjectLinks
  /**
   * The brand mark shown left of the site title in the header. One of:
   * - the name of a known icon (from `icons`), e.g. `'tosiUi'`
   * - an image URL / path / data: URI (rendered as an `<img>`)
   * - a raw inline `<svg …>…</svg>` string
   * Omit to fall back to the tosijs-ui logo when `projectLinks.tosijs` is set,
   * or to no mark at all otherwise. Size and spacing are CSS-tunable via
   * `--tosi-logo-mark-size` (default 32px) and `--tosi-logo-mark-gap` (default
   * 10px) on the `.logo-mark` element.
   */
  logo?: string
  navSize?: number
  minSize?: number
  routing?: DocRoutingMode
  /**
   * Memory routing only: the slug to show first (instead of the page URL / first
   * doc). Lets a host mount the browser already pointed at a specific doc.
   */
  initialRoute?: string
  /**
   * Path routing only: extra paths a doc is served at, `{ alias: slug }` (`''` is the root
   * page). The site build passes its `aliases` here, so `/readme/` opens the README.
   */
  aliases?: Record<string, string>
  /**
   * Memory routing only: called with the current doc's slug whenever in-app
   * navigation happens, so a host can reflect it (e.g. to an attribute).
   */
  onRouteChange?: (slug: string) => void
  /**
   * The key that opens the search palette from anywhere on the page, in `<tosi-palette>`'s
   * `shortcut` form. Default `'mod+k'` (Cmd-K on a Mac, Ctrl-K elsewhere); `''` binds none,
   * for a host page that owns that key or embeds more than one browser.
   */
  searchShortcut?: string
  /**
   * The hint in the header's search field (default `'Search'`). Localized through the site's
   * strings, like every other piece of the doc system's own text.
   */
  searchPlaceholder?: string
  /**
   * `SiteConfig.accessibility` (#3521): render figures with captions and exactly one `<h1>`
   * per page, as the static build does, so a hydrated page matches it.
   */
  accessibility?: AccessibilityMode
  /**
   * Header-bar links. When provided, these replace the legacy `projectLinks` icon
   * set in the header (each renders as an icon if `icon` names a known icon, else
   * as its text label). `projectLinks` is still used for the logo and view-source.
   */
  navbarLinks?: LinkItem[]
  /**
   * Pre-rendered content for the landing doc to ADOPT in place (true hydration).
   * When provided, the current page's already-rendered markdown is left untouched
   * — only live examples are wired up — instead of being re-rendered from text.
   * Used by <tosi-doc-system>. Subsequent navigation renders from doc text.
   */
  contentElement?: HTMLElement
}

// The brand mark shown left of the site title. `logo` (from config) may name a
// known icon, be an inline `<svg>` string, or be an image URL; when absent we
// keep the legacy behavior — the tosijs-ui logo iff this is a tosijs project,
// else nothing. Size and spacing are class-driven (`.logo-mark`, see
// logoMarkStyleSpec) so a consumer retunes them with one CSS variable each,
// rather than a hard-wired pixel value.

// `icons` is a Proxy that returns a factory for ANY string (unknown names get a
// placeholder glyph), so we can't ask it whether a name is real — we route by the
// shape of the string instead: an inline `<svg>` renders as-is, a URL/path/data:
// URI renders as an <img>, and anything else is treated as an icon(-composition)
// name.
function looksLikeImageUrl(s: string): boolean {
  return (
    /^(https?:)?\/\//.test(s) ||
    s.startsWith('data:') ||
    s.startsWith('/') ||
    s.startsWith('./') ||
    s.startsWith('../') ||
    /\.(png|jpe?g|gif|webp|avif|svg|ico)(\?.*)?$/i.test(s)
  )
}
function logoMark(
  logo: string | undefined,
  projectLinks: ProjectLinks
): Element {
  let mark: Element | null = null
  if (logo) {
    if (logo.trimStart().startsWith('<')) {
      // Inline <svg>: wrap in a flex holder so `.logo-mark`'s height sizes it.
      mark = span({ style: { display: 'inline-flex', alignItems: 'center' } })
      mark.innerHTML = logo
    } else if (looksLikeImageUrl(logo)) {
      mark = img({ src: logo, alt: '' })
    } else {
      mark = (icons as any)[logo]()
    }
  } else if (projectLinks.tosijs) {
    mark = icons.tosiUi()
  }
  // No mark → an empty span with NO class, so it carries no margin (the title
  // stays flush; a flex `gap` on the header link couldn't do that).
  if (!mark) return span()
  mark.classList.add('logo-mark')
  return mark
}

/** Below this many pixels of header, the header links move into the settings menu. */
const HEADER_LINKS_COLLAPSE_PX = 600

// The search, in the header bar: the site's pill-shaped search field while there is room, its
// icon alone when the header is squeezed (the palette compacts itself below `compactWidth`).
const navSearchStyleSpec: XinStyleSheet = {
  'header.-links-collapsed > .header-link': {
    display: 'none',
  },
  'header > tosi-palette.doc-search': {
    display: 'flex',
    flex: '0 1 16em',
    minWidth: vars.touchSize,
    margin: `0 ${vars.spacing50}`,
  },
  'header > tosi-palette.doc-search:not(.-icon) > .palette-trigger': {
    borderRadius: 99,
    background: vars.inputBg,
    boxShadow: vars.inputBorderShadow,
    color: vars.placeholderColor,
    lineHeight: 'calc(var(--line-height) + var(--spacing))',
    padding: '0 var(--spacing)',
  },
}

const logoMarkStyleSpec: XinStyleSheet = {
  '.logo-mark': {
    height: 'var(--tosi-logo-mark-size, 32px)',
    // An icon <svg> takes its height from --tosi-icon-size, set INLINE on the svg
    // by makeIcon() — which beats a class `height` rule. Point that knob at the
    // same variable so one --tosi-logo-mark-size drives an icon, an <img>, or an
    // inline <svg> alike.
    _tosiIconSize: 'var(--tosi-logo-mark-size, 32px)',
    marginRight: 'var(--tosi-logo-mark-gap, 10px)',
    flexShrink: '0',
  },
  // Inline-<svg> logos are wrapped in a flex holder; scale the embedded svg to it.
  '.logo-mark > svg': { height: '100%', width: 'auto' },
}

/*
A code block that scrolls sideways must be reachable by keyboard, or its overflow cannot be
read without a pointer (WCAG 2.1.1, axe `scrollable-region-focusable`, #3520). Every static
block gets it, not only the ones overflowing now: overflow changes with the window, and a
check at render time would go stale on the first resize. Done here rather than in the
markdown renderer so the pre-rendered page stays byte-identical to what it was.
*/
export function focusableCodeBlocks(root: ParentNode): void {
  for (const pre of root.querySelectorAll('pre')) {
    if (pre.hasAttribute('tabindex') || pre.closest('tosi-example')) continue
    pre.tabIndex = 0
  }
}

// Monotonic per-page counter so each createDocBrowser() call gets a distinct
// tosi() registry key (see stateKey below) and two browsers can't share state.
let docBrowserSeq = 0
// Likewise for the nav's aria-controls ids.
let navIdCounter = 0

export function createDocBrowser(options: DocBrowserOptions): HTMLElement {
  const {
    docs,
    context = {},
    projectName = '',
    projectLinks = {},
    logo,
    navSize = 200,
    minSize = 600,
    routing = 'query',
    initialRoute,
    aliases = {},
    onRouteChange,
    searchShortcut = 'mod+k',
    searchPlaceholder = 'Search',
    accessibility,
    navbarLinks,
    contentElement,
  } = options

  // Memory routing is fully self-contained: it never reads or writes
  // window.history/location, so an embedded or nested browser can't hijack the
  // host page's URL (or recurse into it).
  const memoryRouting = routing === 'memory'

  // Initialize testStatus on all docs so tosi can track it
  for (const doc of docs) {
    doc.testStatus = undefined
  }

  // Routing abstraction — keeps the legacy `?filename` SPA behavior the default,
  // while letting <tosi-doc-system> drive clean `/slug/` URLs off the same docs.
  // Both path and memory routing key off slugs; only legacy query routing doesn't.
  const slugMap = routing === 'query' ? {} : buildSlugMap(docs)
  const wikilinks = wikilinkTargets(docs)
  const slugFor = (filename: string): string => slugMap[filename] ?? filename
  const filenameForSlug = (slug: string): string => {
    for (const d of docs) if (slugFor(d.filename) === slug) return d.filename
    return ''
  }
  const hrefFor = (filename: string): string =>
    routing === 'query' ? `?${filename}` : pathForSlug(slugFor(filename))
  const filenameFromLocation = (): string => {
    // Memory routing ignores the page URL entirely — it starts at initialRoute.
    if (memoryRouting) return initialRoute ? filenameForSlug(initialRoute) : ''
    return routing === 'path'
      ? filenameForPath(document.location.pathname, slugMap, aliases)
      : document.location.search !== ''
      ? document.location.search.substring(1).split('&')[0]
      : ''
  }

  // Resolve a content-link anchor to one of our docs' filenames, in either the
  // legacy `?filename` form or a clean `/slug/` path — or null for external
  // links, in-page anchors, mailto:, downloads, etc. Used to navigate internal
  // doc links client-side (via go()) instead of triggering a full page reload,
  // and to canonicalize their hrefs for the current routing mode.
  const docFilenameForHref = (anchor: HTMLAnchorElement): string | null => {
    const raw = anchor.getAttribute('href') || ''
    if (raw === '' || raw.startsWith('#')) return null
    let url: URL
    try {
      url = new URL(raw, document.location.href)
    } catch {
      return null
    }
    if (url.origin !== document.location.origin) return null // external
    const known = (filename: string): string | null =>
      docs.some((doc) => doc.filename === filename) ? filename : null
    // Legacy `?filename` form: a single bare filename, not a real query string.
    const query = url.search.replace(/^\?/, '')
    if (query !== '' && !query.includes('=') && !query.includes('&')) {
      const filename = known(decodeURIComponent(query))
      if (filename) return filename
    }
    // Clean `/slug/` path form (only meaningful when slugs are in play).
    if (!memoryRouting && routing !== 'query') {
      const filename = known(filenameForPath(url.pathname, slugMap, aliases))
      if (filename) return filename
    }
    return null
  }

  const docName =
    filenameFromLocation() || homeDoc(docs) || docs[0]?.filename || 'README.md'

  const currentDoc = docs.find((doc) => doc.filename === docName) || docs[0]

  // tosi() registers its top-level keys in a GLOBAL observable registry, so two
  // doc browsers on one page (e.g. a page whose live example embeds a nested
  // <tosi-doc-system>) would BOTH register `app` and share it — the second wins,
  // so the outer browser's nav / currentDoc / edit-source silently read the
  // nested one's doc. Give each instance its own key. (This is the same collision
  // the live-example docs warn about: never register a generic `app`.)
  const stateKey = `docBrowser${docBrowserSeq++}`
  const state = tosi({
    [stateKey]: {
      docs,
      currentDoc,
      compact: false,
      /*
      Whether to OFFER the navigation button — deliberately not the same thing as `compact`.

      The button used to ride `compact` directly, which is right for a responsive page: wide
      enough to show the nav beside the content, and there is nothing to toggle. But opening the
      nav on a full-screen page leaves compact mode by design, and the button would vanish with
      it — stranding the reader in the normal layout with no way back to full screen. So it is
      offered whenever the layout is compact OR the page asked to be full-screen.
      */
      navToggle: false,
    },
  })
  const app = state[stateKey]

  // Assigned by the hierarchical nav builder (path routing); re-applies current
  // highlight, test status, search visibility, and auto-open imperatively.
  // resetOpen=true collapses every section except the current doc's.
  let refreshNav: (resetOpen?: boolean) => void = () => {}

  // Test result tracking
  const pageTestResults: Record<string, PageTestResults> = {}
  // The per-example results behind the entry for the page the reader is on.
  let liveResults = new Map<Element, TestResults>()
  let liveResultsFile = ''
  let testResultsResolve: ((results: DocTestResults) => void) | undefined
  let backgroundTestsStarted = false
  let pagesWithTests = 0
  let pagesTested = 0
  // Per-page budget for the background runner; see the wait loop for why it exceeds 30s.
  const PAGE_DEADLINE_MS = 45_000

  // Set up global promise for scriptable browser integration. A memory-routed
  // (embedded) browser must not clobber the host page's global.
  if (!memoryRouting) {
    window.__docTestResults = new Promise((resolve) => {
      testResultsResolve = resolve
    })
  }

  const updateDocTestStatus = (filename: string) => {
    const results = pageTestResults[filename]
    // Callback receives bare object, return is proxy - cast to work with both
    const doc = (app.docs as unknown as Doc[]).find(
      (d) => d.filename === filename
    )
    if (doc) {
      doc.testStatus = results
        ? results.passed
          ? 'passed'
          : 'failed'
        : undefined
    }
    refreshNav()
  }

  const checkAllTestsComplete = () => {
    if (pagesTested >= pagesWithTests && testResultsResolve) {
      const allResults: DocTestResults = {
        passed: 0,
        failed: 0,
        pages: pageTestResults,
        pagesWithTests,
        pagesTested,
      }

      for (const pageResults of Object.values(pageTestResults)) {
        allResults.passed += pageResults.totalPassed
        allResults.failed += pageResults.totalFailed
      }

      testResultsResolve(allResults)
      testResultsResolve = undefined

      // Post results to dev server on localhost (not from test iframes)
      if (isLocalhost && !isTestFrame) {
        fetch('/report', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(allResults),
        }).catch(() => {
          // Ignore errors - server may not support this endpoint
        })
      }
    }
  }

  const isLocalhost =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'

  const handleTestComplete = (event: CustomEvent) => {
    const { results } = event.detail as {
      results: TestResults
      element: LiveExample
    }
    const filename = String(app.currentDoc.filename)

    /*
    One entry per EXAMPLE, added up — `testcomplete` fires once for each. This used to
    replace the page's entry on every event, so the last example to finish was the whole
    page: a failing first example followed by a passing second one reported green.

    Keyed by element, so an example that re-runs (an edit, a refresh) replaces its own
    result and nothing else's. A different page starts a fresh tally, and elements no longer
    in the document are dropped first.
    */
    if (liveResultsFile !== filename) {
      liveResultsFile = filename
      liveResults = new Map()
    }
    // Navigating away and back re-creates the page's examples; the old elements are gone
    // from the document and their results must go with them, or the page counts double.
    for (const element of liveResults.keys()) {
      if (!element.isConnected) liveResults.delete(element)
    }
    liveResults.set(event.detail.element as Element, results)
    pageTestResults[filename] = tallyExamples(liveResults.values())

    updateDocTestStatus(filename)
  }

  /*
  When has the page now rendered in THIS document finished its tests? Resolves with the
  examples that never settled (empty when all did).

  One definition, used by a test iframe to signal its parent, by the page the reader is on,
  and by a deployed page — the latter two used to be a flat timer, which marked a page tested
  whether or not anything had reported.

  The census is only as good as the DOM it reads. Examples are inserted into the page as it
  renders, so a census taken while insertion is still in progress sees FEWER examples than the
  page has — and an example that appears afterwards was never waited on, never reported, and
  never missed. So the count must be observed to STOP GROWING before "done" means anything:
  two consecutive equal censuses, and a third example arriving later restarts the requirement.
  */
  const whenPageSettled = (deadlineMs: number): Promise<Element[]> =>
    new Promise((resolve) => {
      const startedAt = Date.now()
      let previousCount = -1
      let stableCensuses = 0
      const census = () => {
        const examples = [...container.querySelectorAll('tosi-example')]
        if (examples.length === previousCount) stableCensuses++
        else stableCensuses = 0
        previousCount = examples.length

        const stalled = unsettledExamples(
          examples.map((ex) => ({
            element: ex,
            test: (ex as any).test,
            hasTests: ex.classList.contains('-has-tests'),
            testRunning: ex.classList.contains('-test-running'),
          }))
        )
        const settling = stalled.length > 0 || stableCensuses < 2
        if (settling && Date.now() - startedAt < deadlineMs) {
          setTimeout(census, 100)
          return
        }
        resolve(stalled.map(({ element }) => element))
      }
      // Give the page a moment to insert its examples before the first census.
      setTimeout(census, 500)
    })

  /*
  The page the reader is on runs its tests in place. Wait for them the way a test iframe is
  waited for, then close the entry by the same rule (`closePage`): examples that never
  settled, or no results at all, are failures with a name.
  */
  const settleCurrentPage = async (filename: string): Promise<void> => {
    const stalled = await whenPageSettled(PAGE_DEADLINE_MS)
    /*
    If the reader navigated away, the census was of ANOTHER page and says nothing about this
    one. Whatever this page had reported stands, and if it reported nothing no failure is
    invented for it: the report then has one page fewer than it should, which is the honest
    statement ("we did not look") and is what `testReportVerdict` fails on.
    */
    if (String(app.currentDoc.filename) === filename) {
      pageTestResults[filename] = closePage(pageTestResults[filename], {
        timedOut: false,
        deadlineMs: PAGE_DEADLINE_MS,
        stalled: stalled.length,
      })
      updateDocTestStatus(filename)
    }
    markPageTested(filename)
  }

  // Track when a page finishes loading all its tests
  const markPageTested = (_filename: string) => {
    pagesTested++
    checkAllTestsComplete()
    updateTestWidget()
  }

  bindings.docLink = {
    toDOM(elt, filename) {
      elt.setAttribute('href', hrefFor(filename))
      ;(elt as HTMLElement).dataset.filename = filename
    },
  }

  bindings.current = {
    toDOM(elt, currentFile) {
      elt.classList.toggle(
        'current',
        currentFile === (elt as HTMLElement).dataset.filename
      )
    },
  }

  bindings.testStatus = {
    toDOM(elt, status) {
      elt.classList.remove('-test-passed', '-test-failed')
      if (status === 'passed') {
        elt.classList.add('-test-passed')
      } else if (status === 'failed') {
        elt.classList.add('-test-failed')
      }
    },
  }

  /*
  Search is a palette (board #3454): the field in the nav opens a centred modal of ranked
  results, and you navigate from those. It no longer filters the sidebar, which hid pages
  without ranking any (the maintainer's call, 2026-10-21). The index is built on first use:
  a site that is never searched never lexes its corpus for headings.
  */
  let searchIndex: ReturnType<typeof buildDocIndex> | null = null
  const crumbsFor = (doc: any): string => {
    const parent = doc.parent
      ? docs.find(
          (d: any) =>
            d.filename === doc.parent || slugFor(d.filename) === doc.parent
        ) ?? { title: doc.parent }
      : null
    return parent ? `${parent.title} › ${doc.title}` : String(doc.title)
  }
  const openDoc = (filename: string, anchor?: string) => {
    // go() refuses when the reader keeps unsaved source edits: then the URL and scroll
    // position stay exactly where they were (#3484).
    if (!go(filename) || !anchor) return
    if (!memoryRouting) {
      window.history.replaceState(
        { href: hrefFor(filename) },
        '',
        `${hrefFor(filename)}#${anchor}`
      )
    }
    requestAnimationFrame(() => {
      docContent
        .querySelector(`#${CSS.escape(anchor)}`)
        ?.scrollIntoView({ block: 'start' })
    })
  }
  const docSearchSource = (query: string): PaletteResult[] => {
    searchIndex ??= buildDocIndex(docs as any)
    return searchDocs(searchIndex, query, 30).flatMap((hit) => {
      const doc = hit.doc as any
      const page: PaletteResult = {
        title: String(doc.title),
        description: hit.snippet,
        path: crumbsFor(doc),
        icon: 'fileText',
        kind: 'page',
        score: hit.score,
        action: () => openDoc(doc.filename),
      }
      if (!hit.heading) return [page]
      return [
        {
          title: hit.heading.text,
          description: `In ${doc.title}`,
          path: `${crumbsFor(doc)} › ${hit.heading.text}`,
          icon: 'hash',
          kind: 'section',
          score: hit.score + 1,
          action: () => openDoc(doc.filename, hit.heading!.id),
        },
        page,
      ]
    })
  }
  const quickLinks: PaletteResult[] = docs
    .filter((d: any) => !d.hidden && !d.parent && !d.home)
    .slice(0, 8)
    .map((d: any) => ({
      title: String(d.title),
      icon: 'fileText',
      action: () => openDoc(d.filename),
    }))
  /*
  What the search shows in the header: the field when the header has room, its icon when the
  header is narrow (the same width at which the header links fold into the menu), nothing on a
  header:false page, where only its shortcut opens it.
  */
  let searchHeaderless = false
  const updateSearchTrigger = () => {
    searchBox.trigger = searchHeaderless
      ? 'none'
      : headerBar?.classList.contains('-links-collapsed')
      ? 'icon'
      : 'field'
  }
  // In the header bar (see headerContent); the nav no longer holds a search field.
  const searchBox = tosiPalette({
    class: 'doc-search',
    placeholder: searchPlaceholder,
    localizer: localize,
    shortcut: searchShortcut,
    sources: [docSearchSource],
    quickLinks,
  })
  // The hint follows the reader's language, like the rest of the doc system's own text.
  i18n.locale.observe(() => searchBox.queueRender())

  // Memory routing is decoupled from the page URL, so it ignores browser
  // back/forward (the host owns history, if any).
  if (!memoryRouting) {
    window.addEventListener('popstate', () => {
      // Back/forward while the source editor is open with unsaved changes: the
      // URL already moved, so on "keep editing" we push it back to match the
      // still-open editor; on discard we close the editor and navigate.
      if (editUI) {
        if (
          editorHasUnsavedChanges() &&
          !window.confirm('Discard unsaved changes to the source?')
        ) {
          const href = hrefFor(String(editUI.doc.filename))
          window.history.pushState({ href }, '', href)
          return
        }
        closeEditor()
      }
      /*
      Only re-render when the DOCUMENT changed (#152).

      Setting `location.hash` is a fragment navigation, and per spec that fires `popstate`
      before `hashchange` — so any component keeping state in the hash used to re-mount the
      whole article on every write. `navigateTo` ends at
      `docContent.innerHTML = renderDocMarkdown(...)`, which destroys and re-creates EVERY
      live example on the page: an editor loses focus and its buffer mid-keystroke.

      The filename is the identity that matters. A hash-only change keeps the same document,
      so there is nothing to render — `scrollToHashExample` below still handles the anchor.
      */
      const next = filenameFromLocation()
      // `app.currentDoc.filename` is a BoxedScalar — coerce, or `===` compares a proxy to
      // a string and is never equal, which would make this guard silently do nothing.
      if (next === String(app.currentDoc.filename)) {
        scrollToHashExample()
        return
      }
      navigateTo(next)
    })

    // Reload / tab-close / external navigation: native "leave site?" prompt when
    // there are unsaved source edits (the only guard the browser allows here).
    window.addEventListener('beforeunload', (event: BeforeUnloadEvent) => {
      if (editorHasUnsavedChanges()) {
        event.preventDefault()
        event.returnValue = ''
      }
    })
  }

  // This instance's own sidenav. Captured from its own change event (below), so the
  // header nav toggle drives THIS doc-browser — not `document.querySelector('tosi-
  // sidenav')`, which returns the FIRST sidenav in the document (an OUTER instance
  // when this one is an embedded, memory-routed browser).
  let sidenav: TosiSidenav | null = null
  /*
  `full-screen` uses the sidenav's OWN compact behaviour rather than a new state.

  `<tosi-sidenav>` already does exactly what full-screen wants: compact mode shows the nav OR the
  content, with `contentVisible` picking which. `alwaysCompact` keeps it in that mode at any
  width, and `contentVisible` gives the page the whole box.

  Remembered rather than applied directly, because the sidenav is not there yet when the first
  doc renders — `sidenav` is captured from its own change event, which fires on a state CHANGE
  and so may never fire at all on a normal-width page.
  */
  let wantsFullScreen = false
  /*
  What we last PUSHED onto the sidenav, so the user's own toggle is not overwritten.

  The first version applied the layout on every call, and `applyFullScreen` is called from the
  sidenav's change handler — so clicking "navigation" flipped `contentVisible`, the sidenav
  changed state, the handler fired, and the layout was re-applied on top. The nav button looked
  live and did nothing. Applying only on TRANSITION means a full-screen page sets itself up once
  and then leaves the reader alone.
  */
  let appliedFullScreen: boolean | null = null
  /** Offer the button when there is something to toggle: a compact layout, or a full-screen page. */
  const refreshNavToggle = () => {
    app.navToggle = (Boolean(sidenav?.compact) || wantsFullScreen) as any
  }
  const applyFullScreen = () => {
    /*
    Resolved from THIS browser's own container when the change event has not introduced it yet.
    Scoped rather than `document.querySelector('tosi-sidenav')`, which returns the first in the
    document — for a nested doc-browser, the HOST's.
    */
    if (!sidenav && typeof container !== 'undefined' && container) {
      sidenav = container.querySelector(
        TosiSidenav.tagName!
      ) as TosiSidenav | null
    }
    if (!sidenav) return
    if (appliedFullScreen === wantsFullScreen) return
    /*
    ASYMMETRIC on purpose, and the symmetric version was a real regression.

    Entering full-screen asks for the nav to be away, and `navVisible` is the right control:
    the sidenav decides how that is achieved at the current width.

    LEAVING it must only undo `alwaysCompact`. Writing `navVisible = true` looks like the tidy
    mirror image, and it clobbers `contentVisible` — which on a narrow screen is owned by the
    person reading. Tapping a nav link there is supposed to switch you to the content; the nav
    click handler sets `contentVisible = true` to do exactly that, and this ran afterwards on
    the same navigation and set it straight back to false. The nav stayed up and the article
    never appeared: reported as "touching a link in the sidebar doesn't switch to content
    anymore", and correctly blamed on the full-screen work.

    So: full-screen states what it needs, and stepping out of it states only what it is
    releasing. Whether the nav or the content is showing at a narrow width is not this code's
    business.
    */
    if (wantsFullScreen) sidenav.navVisible = false
    else sidenav.alwaysCompact = false
    appliedFullScreen = wantsFullScreen
    refreshNavToggle()
  }

  const headerContent: any[] = [
    button(
      {
        class: 'iconic',
        style: { color: vars.linkColor },
        title: 'navigation',
        bind: {
          value: app.navToggle,
          binding: {
            toDOM(element, compact) {
              element.style.display = compact ? '' : 'none'
              ;(element.nextSibling as HTMLElement).style.display = compact
                ? ''
                : 'none'
            },
          },
        },
        onClick() {
          // Read it, flip it. `<tosi-sidenav>` resolves what that means at this width; a
          // button should not have to know, and this one used to.
          if (sidenav) sidenav.navVisible = !sidenav.navVisible
        },
      },
      icons.menu()
    ),
    span({ style: { flex: '0 0 10px' } }),
  ]

  if (projectName) {
    headerContent.push(
      a(
        {
          href: '/',
          style: {
            display: 'flex',
            alignItems: 'center',
            borderBottom: 'none',
          },
        },
        logoMark(logo, projectLinks),
        // Not a heading: the site name above every page made it each page's first heading,
        // an <h2> ahead of the page's own <h1> (#3521).
        span({ class: 'site-name' }, projectName)
      )
    )
  }

  headerContent.push(span({ class: 'elastic' }), searchBox)

  // A header link renders as an icon (if `icon` names a known icon) or its label.
  const headerLink = (link: LinkItem) => {
    const iconFactory = link.icon ? (icons as any)[link.icon] : undefined
    return a(
      {
        class: iconFactory ? 'iconic header-link' : 'header-link',
        title: link.label,
        target: '_blank',
        href: link.href,
      },
      iconFactory ? iconFactory() : link.label
    )
  }

  // Every header link, so the settings menu can list them when the header is too narrow.
  const headerLinks: LinkItem[] = []
  if (navbarLinks) {
    // Configurable link set.
    navbarLinks.forEach((link) => {
      headerLinks.push(link)
      headerContent.push(headerLink(link))
    })
  } else {
    // Legacy: derive header icons from the known `projectLinks` keys.
    const legacy: Array<[string | undefined, string, string]> = [
      [projectLinks.tosijs, 'tosi', 'tosijs'],
      [projectLinks.discord, 'discord', 'discord'],
      [projectLinks.blog, 'blog', 'blog'],
      [projectLinks.github, 'github', 'github'],
      [projectLinks.npm, 'npm', 'npmjs'],
    ]
    for (const [href, icon, label] of legacy) {
      if (href) {
        headerLinks.push({ href, label, icon })
        headerContent.push(headerLink({ href, label, icon }))
      }
    }
  }

  // The rendered-markdown content area. When hydrating a static page we ADOPT the
  // pre-rendered node so the landing page's HTML is never re-rendered; otherwise we
  // render from doc text. Every navigation funnels through navigateTo() -> showDoc().
  const docContent = contentElement || div()
  docContent.classList.add('doc-content')
  Object.assign(docContent.style, {
    display: 'block',
    // Same var the pre-hydration `:not(:defined)` layout uses (doc-system-styles.ts).
    // This node is ADOPTED from the pre-rendered page, so if the two boxes disagree
    // the content jumps the instant we hydrate. The fallback keeps a standalone
    // doc-browser (no doc-system stylesheet) working.
    maxWidth: 'var(--doc-content-max-width, 44em)',
    margin: 'auto',
    /*
    Through a variable, for the same reason `maxWidth` is: this is an INLINE style, so a
    stylesheet cannot override it, and `layout: "full-screen"` needs to. A full-screen page that
    keeps a 1em gutter is not full screen — it is a demo inset by 32px, which is exactly what it
    looked like.
    */
    padding: 'var(--doc-content-padding, 0 1em)',
    /*
    Also through a variable, and for a second reason beyond `layout: "full-screen"` needing
    `auto` (tosijs-ui#119).

    `overflow: hidden` on an ancestor kills `position: sticky` in its subtree. A page whose
    whole point is a sticky viewport-sized window inside the article — a scroll engine, a
    landing hero — could only get one with `.doc-content { overflow: visible !important }`,
    and an `!important` in a consumer's stylesheet is this component failing to offer a seam.
    */
    overflow: 'var(--doc-content-overflow, hidden)',
  })

  // Adoption is zero-flash hydration of a statically pre-rendered page: it only
  // holds for the page's own path-routed instance, whose static `.doc-content`
  // matches the initial doc. A memory-routed embed's initial doc
  // (initialRoute/docs[0]) has no relation to whatever happens to sit inside the
  // host element (often an empty placeholder), so always render it fresh.
  // Canonicalize internal content links to the current routing mode's href and
  // tag them — so copy-link / right-click / no-JS land on the right page, and a
  // left-click can be short-circuited to client-side nav. Authored docs use the
  // routing-agnostic `?filename` form (the only one resolvable in every mode);
  // this rewrites them to `/slug/` on the static path-routed site, etc.
  const rewriteContentLinks = (): void => {
    for (const anchor of docContent.querySelectorAll('a')) {
      const filename = docFilenameForHref(anchor as HTMLAnchorElement)
      if (filename !== null) {
        anchor.setAttribute('href', hrefFor(filename))
        ;(anchor as HTMLElement).dataset.docLink = filename
      }
    }
  }

  // Deep-link to a specific live example: arriving at /slug/#example-2 (or a
  // custom ```js#my-id anchor) scrolls it into view with a brief highlight. The
  // example ids are set by insertExamples, so this runs after it. Skipped in
  // memory routing (which must never touch window.location).
  const scrollToHashExample = (): void => {
    if (memoryRouting) return
    let hash = location.hash.replace(/^#/, '')
    if (!hash) return
    try {
      // A heading id can hold non-ASCII letters, which arrive percent-encoded.
      hash = decodeURIComponent(hash)
    } catch {
      // a stray % — look it up as written
    }
    requestAnimationFrame(() => {
      let el: Element | null = null
      try {
        el = docContent.querySelector(`#${CSS.escape(hash)}`)
      } catch {
        el = null
      }
      if (!el) return
      // A heading (they have ids as of 1.16.11) goes to the top, where a reader expects a
      // section to start; an example is centred and flashed.
      if (/^H[1-6]$/.test(el.tagName)) {
        el.scrollIntoView({ block: 'start' })
        return
      }
      el.scrollIntoView({ block: 'center', behavior: 'smooth' })
      el.classList.add('example-target')
      setTimeout(() => el && el.classList.remove('example-target'), 1600)
    })
  }

  let adoptInitialContent = contentElement !== undefined && !memoryRouting
  const showDoc = (doc: Doc) => {
    /*
    Adopt the pre-render only if it IS this doc. The dev server's SPA fallback serves the root
    page for a path with no file of its own, so `/guide/` can arrive holding the home page's
    article (#3346). The generator stamps `data-doc`; a page without it predates the stamp
    and is adopted as before.
    */
    const prerendered = contentElement?.dataset.doc
    const adopt =
      adoptInitialContent &&
      (prerendered === undefined || prerendered === doc.filename)
    adoptInitialContent = false
    if (adopt) {
      // leave the pre-rendered HTML untouched
    } else {
      // Pass the doc's build-time tjs bakes so this client-rendered page embeds the
      // same hidden transpiled <script>s the pre-rendered page has — examples run
      // without loading the tjs transpiler. See self-contained-examples-plan.md.
      docContent.innerHTML = renderDocMarkdown(doc.text, {
        bakes: doc.bakes ? new Map(doc.bakes) : undefined,
        wikilinks,
        accessible: !!accessibility,
        title: doc.title,
      })
    }
    /*
    Keep the page's layout in step with the doc on SPA navigation.

    The static generator stamps `data-layout` so the first paint is already right without JS;
    this is the other half — navigating from a full-width page to a prose one must put the
    reading column back, and vice versa. Written on every showDoc rather than only on change,
    because the adopted first page also arrives here and the attribute it was served with must
    survive rather than be assumed.
    */
    /*
    On the SAME element the static generator stamps — `<tosi-doc-system>` — not on
    `docContent.parentElement`.

    Those are not the same node, which cost a debugging round: hydration wraps the adopted
    `<article class="doc-content">` in a div, so the parent is that div while the generator's
    attribute sits on the custom element above it. Writing to the div left the real host still
    reading `full-width`, so navigating from the wide page to a prose one silently stayed wide —
    and the test that only checked the wide direction would have passed.

    A memory-routed browser is EXCLUDED deliberately. That mode exists so an embedded or nested
    browser cannot reach out and change the page hosting it, and `closest()` from inside one
    finds the OUTER doc-system — the host's, not its own. Same reasoning as its history/location
    ban; a nested doc-browser rewriting its host's layout is the same bug wearing a hat.
    */
    if (!memoryRouting) {
      const layoutHost = docContent.closest('tosi-doc-system')
      if (layoutHost) {
        if (doc.layout) layoutHost.setAttribute('data-layout', doc.layout)
        else layoutHost.removeAttribute('data-layout')
        layoutHost.toggleAttribute('data-no-header', doc.header === false)
      }
      /*
      `full-screen` also collapses the nav, and that has to go through the COMPONENT rather than
      CSS: `<tosi-sidenav>` writes its column widths as inline properties, so a stylesheet has
      nothing to override. The pre-hydration rule in doc-system-styles.ts handles the static
      markup, which has a plain `<nav>` and no sidenav yet; this handles the hydrated page. Two
      rules for two genuinely different DOMs, not two fixes for one bug.
      */
      wantsFullScreen = doc.layout === 'full-screen'
      /*
      Forced on every navigation. Without this, opening the nav on one full-screen page (which
      turns `alwaysCompact` off) would leave the NEXT full-screen page not full-screen, because
      the wish had not changed and the transition check would skip it. A reader's override is
      meant to last until they navigate, not beyond it.
      */
      appliedFullScreen = null
      applyFullScreen()
      // applyFullScreen returns early when there is no sidenav yet; the button state still has
      // to follow the doc, so this does not ride on that path succeeding.
      refreshNavToggle()
    }
    placeTestWidget(doc)
    rewriteContentLinks()
    // Stamp each example with its source file (for the source↔doc map). doc.path
    // is the extracted file (.md, or a source file with doc comments).
    LiveExample.insertExamples(docContent, context, doc.path || undefined)
    focusableCodeBlocks(docContent)
    /*
    Highlight whatever is left — the static blocks that are not live examples.

    A no-op on the page you LANDED on: the build already emitted token markup and the pass
    skips anything carrying it, which is what keeps the pre-rendered page and its hydrated
    self byte-identical. It does real work only on client-side navigation, where the markdown
    was rendered here and has never been highlighted. Fire-and-forget because a page that is
    readable but unhighlighted for a frame is strictly better than one that blocks on Prism.
    */
    void highlightBlocks(docContent, {
      liveExampleTag: (LiveExample.tagName as string) || undefined,
      /*
      The policy the CLIENT actually has — read from the global the build stamps, the same
      source `insertExamples` consults.

      This defaulted to `'auto'`, and the new `isLiveFence` filter in `highlightBlocks` then
      skipped all six executable languages on an `opt-in` site. Such a page hard-loads
      highlighted (the build passes `config.liveExamples` correctly) and lost every token the
      moment a reader navigated in-page: B1's exact symptom — 531 tokens down to 23 — brought
      back for the prose/book audience the opt-in setting exists for. Uncaught because this
      repo's own site is `'auto'`.
      */
      policy: examplePolicy(),
    }).catch(() => {})
    scrollToHashExample()
    if (routing === 'path') {
      // The SAME rule the static generator used for this page's <head> (doc-title.ts).
      // This used to re-derive it — ignoring `headTitle` and re-suffixing a title that
      // already ended in the project name — so the home page's title flipped from
      // "tosijs-ui — robust, dependency-free web components" to "tosijs-ui — tosijs-ui"
      // the moment the bundle loaded. Issue #6, and the only thing on the page that
      // visibly moved on hydration.
      document.title = pageTitle(doc, projectName)
    }
  }
  // Always resolve to the RAW doc from the original array — docs reached via the
  // app.docs proxy expose BoxedScalar fields (doc.text), which break marked().
  const navigateTo = (filename: string) => {
    const doc = docs.find((d) => d.filename === filename) || docs[0]
    app.currentDoc = doc as any
    showDoc(doc)
    refreshNav(true)
  }

  // User-initiated navigation: record the new location (history for path/query,
  // the onRouteChange callback for memory) and then render the doc. Every nav
  // click funnels through here so memory mode never touches window.history.
  const go = (filename: string): boolean => {
    // If the source editor is open with unsaved changes, confirm before leaving;
    // on confirm it discards + closes, otherwise navigation is aborted (false).
    if (!confirmLeaveEditor()) return false
    if (memoryRouting) {
      onRouteChange?.(slugFor(filename))
    } else {
      const href = hrefFor(filename)
      /*
      A link to the document already showing REPLACES the entry rather than pushing one, as a
      native same-URL navigation does. Pushing left a duplicate the reader had to Back
      through, and Back then visibly did nothing — the symptom #174 reported.

      "Same" is the DOCUMENT, the identity the popstate guard uses, not the raw path: `/x`,
      `/x/` and `/x/index.html`, or `?x` and `?x&_testMode=1`, are one page. A URL carrying a
      hash still pushes, because leaving `/x/#example` for `/x/` is a navigation the reader
      may want to come Back from.
      */
      const same =
        filename === String(app.currentDoc.filename) &&
        document.location.hash === ''
      window.history[same ? 'replaceState' : 'pushState']({ href }, '', href)
    }
    navigateTo(filename)
    return true
  }

  // Intercept clicks on internal doc links anywhere in the rendered content so
  // they navigate client-side instead of reloading the page. Plain left-clicks
  // only — modified clicks / middle-clicks / new-tab / download links fall
  // through to the browser. Works for both freshly-rendered and statically
  // pre-rendered (adopted) content, resolving the href at click time.
  docContent.addEventListener('click', (event: MouseEvent) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return
    const anchor = (event.target as HTMLElement).closest(
      'a'
    ) as HTMLAnchorElement | null
    if (
      !anchor ||
      anchor.target === '_blank' ||
      anchor.hasAttribute('download')
    )
      return
    const filename = docFilenameForHref(anchor)
    if (filename === null) return
    event.preventDefault()
    go(filename)
  })

  // ── Edit page source (Foundation B / #4) ──────────────────────────────────
  // The "view source" affordance opens a menu: edit the whole source file in a
  // code editor that fills the content area, preview the result in-browser, and
  // save (dev: write the repo file via /__docstore/source; the watcher rebuilds)
  // or download it. Source is read from the dev endpoint, falling back to GitHub
  // raw, so editing works on the deployed site too (save there = download).
  const editorModeFor = (p: string): string =>
    p.endsWith('.md')
      ? 'markdown'
      : p.endsWith('.css')
      ? 'css'
      : p.endsWith('.ts') || p.endsWith('.tjs')
      ? 'typescript'
      : 'javascript'

  // Pure mirror of docs.ts extraction: a .md *is* the markdown; a source file is
  // the concatenation of its doc-comment blocks. Lets us preview an edit in the
  // browser with no rebuild.
  const docMarkdownFromSource = (content: string, p: string): string => {
    if (p.endsWith('.md')) return content
    const blocks = content.match(/\/\*#[\s\S]+?\*\//g) || []
    return blocks.map((s) => s.substring(3, s.length - 2).trim()).join('\n\n')
  }

  const githubRawUrl = (p: string): string | null => {
    const gh = projectLinks.github
    if (!gh || !p) return null
    const m = gh.match(/github\.com\/([^/]+)\/([^/]+?)(?:\.git)?\/?$/)
    return m
      ? `https://raw.githubusercontent.com/${m[1]}/${m[2]}/main/${p}`
      : null
  }

  /*
  UNAUTHORIZED IS NOT "NO ENDPOINT".

  This treated any non-2xx the same and fell through to GitHub raw `main` — so an
  unauthorized reader silently got the PUBLISHED file instead of the working copy the
  page was rendered from, edited that, and then (see saveSourceEdit) was handed a
  download of it. Applying that download to the repo clobbers uncommitted work. A
  wrong answer delivered confidently is worse than an error.

  401/403 now means "you need an invite link", not "this must be a deployed site".
  */
  let sourceUnauthorized = false

  const loadSource = async (p: string): Promise<string | null> => {
    sourceUnauthorized = false
    try {
      const r = await fetch(`/__docstore/source?file=${encodeURIComponent(p)}`)
      if (r.status === 401 || r.status === 403) {
        sourceUnauthorized = true
        return null
      }
      // Accept only a genuine source response. A misconfigured dev server (no
      // editableSources) or a SPA-rewrite host answers an unknown path with
      // index.html at status 200 — taking that would load the PAGE as the source.
      // Reject HTML and fall through to the GitHub raw source instead.
      const contentType = r.headers.get('content-type') || ''
      if (r.ok && !/text\/html/i.test(contentType)) return await r.text()
    } catch {
      // dev endpoint not available — fall through to GitHub raw
    }
    const raw = githubRawUrl(p)
    if (raw) {
      try {
        const r = await fetch(raw)
        if (r.ok) return await r.text()
      } catch {
        // offline / no network
      }
    }
    return null
  }

  /**
   * Returns the HTTP status, not a boolean.
   *
   * A bare boolean made the caller read 403 ("you are not authorized") as 501 ("this
   * is a deployed site with no write endpoint") and silently download the file — the
   * one outcome that can destroy work if the user later applies it over their repo.
   * 0 means the request never completed.
   */
  const saveSourceToDisk = async (
    p: string,
    content: string
  ): Promise<number> => {
    try {
      const r = await fetch('/__docstore/source', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: p, content }),
      })
      return r.status
    } catch {
      return 0
    }
  }

  const downloadText = (filename: string, content: string): void => {
    const url = URL.createObjectURL(new Blob([content], { type: 'text/plain' }))
    const link = a({ href: url, download: filename }) as HTMLAnchorElement
    link.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  let editUI: {
    editor: CodeEditor
    doc: Doc
    view: 'edit' | 'preview' | 'diff'
    // The source as loaded (and re-baselined on save) — compared against the
    // editor value to detect unsaved changes when navigating away.
    original: string
  } | null = null

  const editorHasUnsavedChanges = (): boolean =>
    editUI !== null && editUI.editor.value !== editUI.original

  // Tear the editor down without re-rendering the current doc (the caller is
  // about to navigate). exitEditSource() restores the current doc instead.
  const closeEditor = (): void => {
    if (!editUI) return
    editUI.editor.remove()
    editUI = null
    docContent.style.display = ''
  }

  // Called before in-app navigation: if the editor is open with unsaved changes,
  // confirm discarding them. Returns false to abort navigation.
  const confirmLeaveEditor = (): boolean => {
    if (!editUI) return true
    if (
      editorHasUnsavedChanges() &&
      !window.confirm('Discard unsaved changes to the source?')
    ) {
      return false
    }
    closeEditor()
    return true
  }

  // Switch the source editor between the raw editor, the rendered preview, and a
  // diff of the edits against the loaded original. preview/diff render into the
  // doc-content area; edit shows the CodeEditor.
  const setSourceView = (view: 'edit' | 'preview' | 'diff'): void => {
    if (!editUI) return
    editUI.view = view
    if (view === 'preview') {
      docContent.innerHTML = renderDocMarkdown(
        docMarkdownFromSource(editUI.editor.value, editUI.doc.path),
        { wikilinks, accessible: !!accessibility, title: editUI.doc.title }
      )
      rewriteContentLinks()
      LiveExample.insertExamples(
        docContent,
        context,
        editUI.doc.path || undefined
      )
      focusableCodeBlocks(docContent)
    } else if (view === 'diff') {
      docContent.replaceChildren(
        tosiDiff({
          original: editUI.original,
          modified: editUI.editor.value,
          style: { display: 'block', width: '100%', height: '100%' },
        })
      )
    }
    docContent.style.display = view === 'edit' ? 'none' : ''
    editUI.editor.style.display = view === 'edit' ? 'block' : 'none'
  }

  const exitEditSource = (): void => {
    if (!editUI) return
    const filename = String(app.currentDoc.filename)
    closeEditor()
    navigateTo(filename) // restore the canonical rendered doc
  }

  const saveSourceEdit = async (): Promise<void> => {
    if (!editUI) return
    const { doc, editor } = editUI
    const status = await saveSourceToDisk(doc.path, editor.value)
    if (status >= 200 && status < 300) {
      editUI.original = editor.value // saved — this is the new clean baseline
      setSourceView('preview') // the watcher also rebuilds in the background
    } else if (status === 401 || status === 403) {
      /*
      Do NOT download here. Downloading says "there is nowhere to save this, here is
      the file" — but the file we would hand back was loaded from GitHub `main`, not
      from the working copy, so applying it over the repo silently reverts uncommitted
      work. Say what is actually true and let the user get a link.
      */
      window.alert(
        'This workspace needs an invite link before it can save.\n\n' +
          'Run `tosijs-tunnel --link` on the machine hosting it, open the link once, ' +
          'then try again. Your edit is still here.\n\n' +
          LAN_REFUSAL_ADVICE
      )
    } else {
      // Genuinely no write endpoint (a deployed static site) — hand the file back.
      downloadText(doc.path.split('/').pop() || 'source.txt', editor.value)
    }
  }

  const enterEditSource = async (doc: Doc): Promise<void> => {
    if (editUI) return
    const content = await loadSource(doc.path)
    if (content === null) {
      window.alert(
        sourceUnauthorized
          ? 'This workspace needs an invite link before you can edit.\n\n' +
              'Run `tosijs-tunnel --link` on the machine hosting it and open the link once.\n\n' +
              LAN_REFUSAL_ADVICE
          : `Could not load source for ${doc.path}`
      )
      return
    }
    const editor = codeEditor({ mode: editorModeFor(doc.path) }) as CodeEditor
    // No toolbar — the Source menu carries the edit controls (it adapts while
    // editing), so the editor fills the whole content area.
    editor.style.cssText =
      'display:block; width:100%; height:100%; border:none;'
    editor.value = content
    const container = docContent.parentElement as HTMLElement
    container.append(editor)
    editUI = { editor, doc, view: 'edit', original: content }
    setSourceView('edit')
  }

  /*
  The Source items, separated from the popup that used to be their only home.

  They now hang off the app menu as a submenu, so this returns the ITEMS and lets the
  caller decide where they appear — an empty array when the current doc has no source
  file, which is how the host knows to omit the submenu entirely rather than showing an
  empty one.
  */
  /*
  A Tests toggle, offered only on a page that HAS tests (tosijs-ui#113).

  Tests default to on for localhost and off everywhere else, and when they are off the whole
  widget is hidden — so a page with failing tests looked identical to a page with no tests.
  There was an override (`localStorage['tosijs-ui-tests-enabled']`), but you had to know the
  key, which means you had to already suspect there was something to see.

  The default is right: nobody wants a test widget on a published doc site. It was the
  SILENCE that cost — and it cost most down the path we recommend, since `tosijs-tunnel`
  necessarily serves from a non-localhost hostname, so the sanctioned way to view a dev site
  remotely is exactly where test state goes invisible.

  Gating on the count rather than showing it always is what keeps the published-site default
  honest: a page with no tests offers nothing, and a page with tests says so.
  */
  const testsMenuItem = (): any => {
    const count = pageTestCount()
    if (count === 0) return null
    const on = testManager.enabled.value
    return {
      caption: on ? 'Tests' : `Tests (${count} not run)`,
      icon: on ? 'checkCircle' : 'circle',
      checked: () => testManager.enabled.value,
      action: () =>
        testManager.enabled.value ? disableTests() : enableTests(),
    }
  }

  const sourceMenuItems = (): any[] => {
    const doc = docs.find(
      (d) => String(d.filename) === String(app.currentDoc.filename)
    )
    if (!doc || !doc.path) return []
    const fileName = (path: string) => path.split('/').pop() || path

    // While editing, the Source menu becomes the editor's controls (no separate
    // toolbar). Otherwise it offers entry points: edit, view on GitHub, download.
    const menuItems = editUI
      ? [
          ...(editUI.view !== 'edit'
            ? [
                {
                  caption: 'Edit',
                  icon: 'edit',
                  action: () => setSourceView('edit'),
                },
              ]
            : []),
          ...(editUI.view !== 'preview'
            ? [
                {
                  caption: 'Preview',
                  icon: 'eye',
                  action: () => setSourceView('preview'),
                },
              ]
            : []),
          {
            caption: 'View changes',
            icon: 'code',
            action: () => setSourceView('diff'),
            enabled: () => editorHasUnsavedChanges(),
          },
          null,
          {
            caption: 'Save to source',
            icon: 'save',
            action: () => void saveSourceEdit(),
          },
          {
            caption: 'Download',
            icon: 'download',
            action: () =>
              downloadText(fileName(editUI!.doc.path), editUI!.editor.value),
          },
          null,
          { caption: 'Close editor', icon: 'x', action: exitEditSource },
        ]
      : [
          {
            caption: 'Edit page source',
            icon: 'edit',
            action: () => void enterEditSource(doc),
          },
          ...(projectLinks.github && doc.path && doc.path !== 'README.md'
            ? [
                {
                  caption: 'View on GitHub',
                  icon: 'github',
                  action: () => {
                    window.open(
                      `${projectLinks.github}/blob/main/${doc.path}`,
                      '_blank'
                    )
                  },
                },
              ]
            : []),
          {
            caption: 'Download source',
            icon: 'download',
            action: () =>
              void loadSource(doc.path).then((c) => {
                if (c !== null) downloadText(fileName(doc.path), c)
              }),
          },
        ]

    return menuItems
  }

  // ── Hierarchical nav (path routing) ───────────────────────────────────────
  // Build nested sections from the doc tree; current-highlight, test status,
  // search visibility, and auto-open are applied imperatively by refreshNav.
  const navStyle = {
    slot: 'nav',
    // .doc-nav so the shared nav CSS (list reset, indentation) matches the
    // runtime nav too, not just the static pre-rendered <nav class="doc-nav">.
    class: 'doc-nav',
    style: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: 'calc(100% - 44px)',
      overflowY: 'scroll',
    },
  }

  const buildHierarchicalNav = (): HTMLElement => {
    const roots = buildNavTree(docs as any, slugMap)
    // Ids for aria-controls, unique per browser: an embedded doc browser shares the page.
    const navIdPrefix = `tosi-nav-${(navIdCounter += 1)}-`
    const leaves = new Map<string, { li: HTMLElement; link: HTMLElement }>()
    const branches: {
      li: HTMLElement
      el: { setOpen(open: boolean): void }
      link: HTMLElement
      filename: string
      subtree: string[]
    }[] = []

    const subtreeFilenames = (node: NavNode<any>): string[] => {
      const out = [node.doc.filename]
      for (const c of node.children) out.push(...subtreeFilenames(c))
      return out
    }

    const navClick = (doc: Doc) => (event: Event) => {
      // Use the href from the closure, not the event — the click can land on a
      // child of the <a> (the localized label), so event.currentTarget/target
      // isn't reliably the anchor.
      const nav = (event.target as HTMLElement).closest(
        'tosi-sidenav'
      ) as TosiSidenav | null
      if (nav) nav.contentVisible = true
      go(String(doc.filename))
      event.preventDefault()
      const results = pageTestResults[doc.filename]
      if (results && !results.passed) {
        setTimeout(() => {
          const failed = document.querySelector('tosi-example.-test-failed')
          if (failed)
            failed.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 100)
      }
    }

    const renderNode = (node: NavNode<any>): HTMLElement => {
      const link = a(
        {
          class: 'doc-link',
          href: hrefFor(node.doc.filename),
          onClick: navClick(node.doc),
        },
        tosiLocalized(node.doc.title)
      )
      if (node.children.length === 0) {
        const item = li(link)
        leaves.set(node.doc.filename, { li: item, link })
        return item
      }
      /*
      A toggle button BESIDE the link, not a <summary> around it (#3520): a link inside a
      summary is a control inside a control. Same markup as the static nav
      (`navToggleHtml` in generate-site.ts), so hydration does not move anything.
      */
      const listId = `${navIdPrefix}${node.slug || 'home'}`
      const toggle = button({
        type: 'button',
        class: 'nav-toggle',
        ariaExpanded: 'false',
        ariaControls: listId,
        ariaLabel: String(node.doc.title),
        onClick: () => section.setOpen(!item.classList.contains('-open')),
      })
      const item = li(
        { class: 'nav-branch' },
        div({ class: 'nav-branch-head' }, toggle, link),
        ul({ id: listId }, ...node.children.map(renderNode))
      )
      const section = {
        setOpen(open: boolean) {
          item.classList.toggle('-open', open)
          toggle.setAttribute('aria-expanded', String(open))
        },
      }
      branches.push({
        li: item,
        el: section,
        link,
        filename: node.doc.filename,
        subtree: subtreeFilenames(node),
      })
      return item
    }

    // `-live`: these sections have working toggles, so closed ones collapse even where no
    // pre-paint script marked <html> (a browser embedded outside a generated page).
    const root = div(
      { ...navStyle, class: `${navStyle.class} -live` },
      ul(...roots.map(renderNode))
    )

    const applyStatus = (
      link: HTMLElement,
      filename: string,
      current: string
    ) => {
      link.classList.toggle('current', filename === current)
      const r = pageTestResults[filename]
      link.classList.toggle('-test-passed', !!r && r.passed)
      link.classList.toggle('-test-failed', !!r && !r.passed)
    }

    refreshNav = (resetOpen = false) => {
      // app.currentDoc.filename is a BoxedScalar; coerce so === / includes work.
      const cur = app.currentDoc as any
      const current = cur && cur.filename != null ? String(cur.filename) : ''
      for (const [filename, { link }] of leaves) {
        applyStatus(link, filename, current)
      }
      for (const { el, link, filename, subtree } of branches) {
        applyStatus(link, filename, current)
        // On navigation (resetOpen), collapse everything except the current doc's section.
        // Otherwise (e.g. test-status refresh) leave user toggles alone.
        if (resetOpen) el.setOpen(subtree.includes(current))
        else if (subtree.includes(current)) el.setOpen(true)
      }
    }
    refreshNav(true)
    return root
  }

  const navContent =
    routing !== 'query'
      ? buildHierarchicalNav()
      : div(
          {
            ...navStyle,
            bindList: {
              idPath: 'filename',
              hiddenProp: 'hidden',
              value: app.docs,
              // the flat nav (query routing) lists every doc so the search can filter it and a
              // reader can scan it; the site's author can't reach this binding to say so
              nonVirtualReason:
                'doc navigation: every doc is listed so search can filter it',
            },
          },
          template(
            a(
              {
                class: 'doc-link',
                bindCurrent: `${stateKey}.currentDoc.filename`,
                bindDocLink: '^.filename',
                bindTestStatus: '^.testStatus',
                onClick(event: Event) {
                  const doc = getListItem(event.target as HTMLElement)
                  const nav = (event.target as HTMLElement).closest(
                    'tosi-sidenav'
                  ) as TosiSidenav
                  nav.contentVisible = true
                  go(String((doc as Doc).filename))
                  event.preventDefault()
                },
              },
              tosiLocalized({ bindText: '^.title' })
            )
          )
        )

  const container = div(
    {
      style: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100vw',
        height: '100vh',
        overflow: 'hidden',
      },
    },
    header(...headerContent),
    tosiSidenav(
      {
        name: 'Documentation',
        navSize,
        minSize,
        style: {
          flex: '1 1 auto',
          overflow: 'hidden',
        },
        onChange(event: Event) {
          // Scope to the sidenav that fired — and capture it so the header toggle
          // targets THIS instance, not the first sidenav in the document.
          sidenav = (event.target as HTMLElement).closest(
            TosiSidenav.tagName!
          ) as TosiSidenav | null
          if (sidenav) app.compact = sidenav.compact as any
          refreshNavToggle()
          // The sidenav has just introduced itself; give it the layout the doc asked for.
          applyFullScreen()
        },
      },
      navContent,
      /*
      The page's main landmark, and focusable: a page of plain prose has nothing else to tab
      to, so a keyboard user could not scroll it at all (WCAG 2.1.1; axe
      scrollable-region-focusable, #3575). A role, not a <main>, so styles written against the
      div still apply. An embedded browser (`memory` routing) is part of someone else's page,
      so it does not claim that page's main landmark.
      */
      div(
        {
          ...(routing === 'memory' ? {} : { role: 'main' }),
          tabindex: 0,
          style: {
            position: 'relative',
            overflowY: 'scroll',
            height: '100%',
          },
        },
        docContent
      )
    )
  )

  // Test status widget
  const testWidget = button(
    {
      class: 'test-widget',
      hidden: true,
      onClick: showTestMenu,
    },
    span({ part: 'label' }, 'Tests'),
    span({ class: 'count', part: 'count' }, '0')
  )
  const headerBar = container.querySelector('header')!
  // In the header unless this doc hides the header; see `.test-widget` in the styles.
  const placeTestWidget = (doc: Doc) => {
    const floating = doc.header === false
    testWidget.classList.toggle('-floating', floating)
    const home = floating ? container : headerBar
    if (testWidget.parentElement !== home) home.appendChild(testWidget)
    /*
    The search lives in the header too, and a modal <dialog> inside a display:none header is
    never shown, so ⌘K would do nothing on a header:false page. There it waits outside the
    header with no trigger of its own, opened by its shortcut.
    */
    searchHeaderless = floating
    updateSearchTrigger()
    if (floating && searchBox.parentElement !== container) {
      container.appendChild(searchBox)
    } else if (!floating && searchBox.parentElement !== headerBar) {
      headerBar.querySelector('.elastic')!.after(searchBox)
    }
  }

  // Render the landing doc (adopts pre-rendered HTML when hydrating).
  showDoc(currentDoc)

  // Inject test indicator styles
  StyleSheet('test-indicators', testIndicatorStyleSpec)

  // Header brand-mark sizing (icon / <img> / inline-<svg>), one CSS var each.
  StyleSheet('doc-logo-mark', logoMarkStyleSpec)
  StyleSheet('doc-nav-search', navSearchStyleSpec)
  StyleSheet('doc-nav-sections', navSectionStyleSpec)

  /*
  The far end has something to tell you.

  The dev server injects `window.__tosiDevStatus` at serve time when a rebuild has
  FAILED — see statusSnippet() in site/dev-server.ts. That is the same kind of news
  as a failing browser test (something broke; here is what), so it uses the same
  floating widget rather than a second overlay competing for the same corner.

  Two differences from a test run, both deliberate:
    - it does NOT auto-fade. A passing test run is over; a broken build is a standing
      condition you have to act on, and it stays until a good build clears it.
    - it wins ties. If tests also failed, the build failure is the more useful thing
      to read first — the tests probably failed BECAUSE the build did.

  Nothing is injected when the server is healthy, so this is inert in production and
  in a normal session.
  */
  const devStatus = (globalThis as any).__tosiDevStatus as
    | { ok: boolean; label?: string; detail?: string }
    | undefined
  let devStatusShown = false
  if (devStatus && devStatus.ok === false) {
    devStatusShown = true
    const labelEl = testWidget.querySelector('[part="label"]')
    const countEl = testWidget.querySelector('[part="count"]')
    if (labelEl) labelEl.textContent = devStatus.label || 'Build failed'
    if (countEl) (countEl as HTMLElement).hidden = true
    testWidget.classList.add('-failed', '-dev-status')
    testWidget.hidden = false
    testWidget.title = devStatus.detail || ''
    // The page you are reading is the LAST GOOD build — the dev server keeps serving
    // it rather than the wreckage of the failed one — so say so plainly in the
    // console, where the detail is readable and copyable.
    console.error(
      `[tosi] ${
        devStatus.label || 'Build failed'
      } — showing the last good build.\n` + (devStatus.detail || '')
    )
  }

  let testsRunning = false

  function setTestWidgetRunning() {
    // A broken build outranks a test run: the tests very likely failed BECAUSE the
    // build did, so replacing "Build failed" with "Tests" would hide the cause.
    if (devStatusShown) return
    testsRunning = true
    testWidget.hidden = false
    testWidget.classList.remove('-passed', '-failed')
    testWidget.classList.add('-running')
    updateTestWidgetDisplay()
  }

  function updateTestWidgetDisplay() {
    /*
    A broken build outranks test results, HERE too.

    The guard existed only in setTestWidgetRunning(), but this function rewrites the
    label unconditionally — so seconds after a failed rebuild the chip read "Passed" in
    red: the colour said one thing and the text another, and the actual news (your build
    is broken) was gone. The tests very likely failed BECAUSE the build did.
    */
    if (devStatusShown) return
    const labelEl = testWidget.querySelector('[part="label"]')
    const countEl = testWidget.querySelector('[part="count"]')

    const totalPassed = Object.values(pageTestResults).reduce(
      (sum, r) => sum + r.totalPassed,
      0
    )
    const totalFailed = Object.values(pageTestResults).reduce(
      (sum, r) => sum + r.totalFailed,
      0
    )

    if (labelEl) {
      if (testsRunning) {
        labelEl.textContent = 'Running'
      } else if (totalFailed > 0) {
        labelEl.textContent = 'Failed'
      } else if (totalPassed > 0) {
        labelEl.textContent = 'Passed'
      } else {
        labelEl.textContent = 'Tests'
      }
    }
    if (countEl) {
      countEl.textContent =
        totalFailed > 0 ? String(totalFailed) : String(totalPassed)
    }
  }

  function updateTestWidget() {
    const totalFailed = Object.values(pageTestResults).reduce(
      (sum, r) => sum + r.totalFailed,
      0
    )

    if (testsRunning && pagesTested >= pagesWithTests) {
      // Tests complete
      testsRunning = false
      testWidget.classList.remove('-running')
      if (totalFailed > 0) {
        testWidget.classList.add('-failed')
        testWidget.classList.remove('-passed')
        testWidget.hidden = false
      } else {
        testWidget.classList.add('-passed')
        testWidget.classList.remove('-failed')
        testWidget.hidden = false // settles into a muted badge (see `.test-widget.-passed`)
      }
    }

    updateTestWidgetDisplay()
  }

  function showTestMenu() {
    const failedPages = Object.entries(pageTestResults).filter(
      ([, results]) => !results.passed
    )

    const menuItems: any[] = []

    for (const [filename, results] of failedPages) {
      const doc = docs.find((d) => d.filename === filename)
      const failedTests = results.tests.filter((t) => !t.passed)

      for (const test of failedTests) {
        menuItems.push({
          caption: `${doc?.title || filename}: ${test.name}`,
          action: () => {
            // Navigate to the page
            const docObj = app.docs.find(
              (d: any) => String(d.filename) === filename
            )
            if (docObj) {
              go(filename)
              // Scroll to failing test after render
              setTimeout(() => {
                const failedExample = document.querySelector(
                  'tosi-example.-test-failed'
                )
                if (failedExample) {
                  failedExample.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                  })
                }
              }, 100)
            }
          },
        })
      }
    }

    if (menuItems.length > 0) {
      menuItems.push(null) // separator
    }

    menuItems.push({
      icon: 'copy',
      caption: 'Copy test results to clipboard',
      action: () => {
        const report = generateTestReport()
        navigator.clipboard.writeText(report)
      },
    })

    popMenu({
      target: testWidget,
      menuItems,
    })
  }

  function generateTestReport(): string {
    const lines: string[] = ['# Test Results', '']
    let totalPassed = 0
    let totalFailed = 0

    for (const [filename, results] of Object.entries(pageTestResults)) {
      const doc = docs.find((d) => d.filename === filename)
      const title = doc?.title || filename

      totalPassed += results.totalPassed
      totalFailed += results.totalFailed

      if (results.tests.length > 0) {
        lines.push(`## ${title}`)
        lines.push('')
        for (const test of results.tests) {
          const icon = test.passed ? '✓' : '✗'
          const line = test.error
            ? `- ${icon} ${test.name}: ${test.error}`
            : `- ${icon} ${test.name}`
          lines.push(line)
        }
        lines.push('')
      }
    }

    lines.unshift(
      `**Summary: ${totalPassed} passed, ${totalFailed} failed**`,
      ''
    )

    return lines.join('\n')
  }

  // Detect if running as background test iframe (never for an embedded browser —
  // it shares the host page's URL but isn't the test target).
  const searchParams = new URLSearchParams(window.location.search)
  const isTestFrame = !memoryRouting && searchParams.get('_testMode') === '1'
  const testFrameFilename = isTestFrame ? filenameFromLocation() : null

  // Listen for test completion events
  container.addEventListener('testcomplete', ((event: CustomEvent) => {
    handleTestComplete(event)
    updateTestWidget()

    // If running in test iframe, post results to parent
    if (isTestFrame && window.parent !== window && testFrameFilename) {
      const { results } = event.detail as { results: TestResults }
      window.parent.postMessage(
        { type: 'tosi-test-results', filename: testFrameFilename, results },
        '*'
      )
    }
  }) as EventListener)

  /*
  If running as a test iframe, signal when all tests on this page are done.

  What counts as "done" is decided from each example's `test` SOURCE, which exists as soon
  as the example element does — never from `-has-tests`, which the example only gains after
  its `js` block has finished running.

  That distinction is the whole bug this replaced. The old check was "at least one example
  has tests and none is currently running", so a page reported done the moment its FIRST
  example settled — while any example still awaiting a slow `js` block (a `fetch`, say) had
  neither class yet and was simply not counted. Its tests never ran, were never reported,
  and the lane stayed GREEN: `data-table.ts` silently went from 8 tests to 1 when a second
  fetching example was added to the page, and the suite reported "34 passed" either way.
  A lane that quietly tests less than it claims is worse than one that fails.
  */
  if (isTestFrame && testFrameFilename) {
    // Generous: this bounds a hung example, it does not pace a slow one.
    const STALL_TIMEOUT_MS = 30000
    void whenPageSettled(STALL_TIMEOUT_MS).then((stalled) => {
      /*
      Past the deadline with examples still unsettled: report them as failures rather than
      signalling a done that omits them. An example that never finishes is a real defect —
      usually a `js` block awaiting something that never resolves — and the one thing it
      must not do is disappear.
      */
      if (stalled.length > 0) {
        window.parent.postMessage(
          {
            type: 'tosi-test-results',
            filename: testFrameFilename,
            results: {
              passed: 0,
              failed: stalled.length,
              tests: stalled.map((element) => ({
                name: `example never finished running (${STALL_TIMEOUT_MS}ms)`,
                passed: false,
                error: `The example's code did not complete, so its test block never ran. Its source begins: ${String(
                  (element as any).js ?? ''
                ).slice(0, 120)}`,
              })),
            },
          },
          '*'
        )
      }
      window.parent.postMessage(
        { type: 'tosi-tests-done', filename: testFrameFilename },
        '*'
      )
    })
  }

  // Background test runner for all doc pages
  const runBackgroundTests = async () => {
    if (backgroundTestsStarted) return
    if (!testManager.enabled.value) return
    if (isTestFrame) return // Don't run background tests in test iframe
    backgroundTestsStarted = true

    /*
    Find all docs that have test blocks.

    Pages with examples but no explicit tests are covered at BUILD time by
    `src/doc-system/site/check-examples.ts`, so the runner does not have to render every heavy
    page just to notice a build error.

    That check is a TRANSPILE check, though, and the comment here used to imply more. It
    catches an example that will not COMPILE; it cannot catch one that compiles and then throws
    — `process is not defined` from a Node-flavoured snippet in a `ts` fence builds clean and
    exits 0. So a runtime error in an example on a page with no ` ```test ` block is caught by
    nothing. Tracked as its own issue rather than papered over by making this predicate loose.
    */
    const docsWithTests = docs.filter((doc) =>
      hasTestBlock(doc.text, examplePolicy())
    )
    pagesWithTests = docsWithTests.length

    if (pagesWithTests > 0) {
      setTestWidgetRunning()
    }

    if (pagesWithTests === 0) {
      if (testResultsResolve) {
        testResultsResolve({
          passed: 0,
          failed: 0,
          pages: {},
          pagesWithTests: 0,
          pagesTested: 0,
        })
        testResultsResolve = undefined
      }
      return
    }

    const currentFilename = String(app.currentDoc.filename)

    /*
    Create a hidden iframe that loads the full page. Keep a real layout size — layout-dependent
    example tests need one — and hide it with a clip that leaves nothing to paint, not opacity:
    Chromium (and haltija on top of it) still composites a 0-opacity layer at 0,0, which flashes
    as the frame navigates page to page.

    INSIDE the viewport, though. It used to be parked at left:-10000px, and WebKit throttles a
    frame outside the viewport: its animation frames never came, tosijs renders on animation
    frames, so every page sat until its 45s deadline and Safari showed "Running 0" for the whole
    run. That was the "WebKit's iframe runner never signals" of the e2e lane, and it reached
    readers once the tunnel ran tests in the background.

    THE SIZE IS OVERRIDABLE, because 800x600 was the only width the inline tier had ever run at.
    A doc-system layout regression at phone width shipped in 1.12.3 and was found by a reader,
    not by the suite: every test ran wide, where the two panes show together and the bug is
    invisible. An iframe is a real viewport — `matchMedia` and element sizing answer to its
    dimensions, verified — so re-running the tier narrow costs nothing but the size.

    Only dimensions, deliberately. Touch cannot be faked from inside a page: an iframe inherits
    the host's input characteristics, so `(pointer: coarse)` stays false and `maxTouchPoints`
    stays 0 however small the frame is. `navigator.maxTouchPoints` can be redefined, and doing so
    would be worse than useless — it lies to code that reads the property while the media queries
    still say fine-pointer, so tests would pass on a page behaving as desktop. Touch emulation
    belongs to the Playwright lane, which can set it per context.
    */
    const vp = (globalThis as any).__tosiTestViewport as
      | { width?: number; height?: number }
      | undefined
    const vpWidth = Number(vp?.width) > 0 ? Number(vp?.width) : 800
    const vpHeight = Number(vp?.height) > 0 ? Number(vp?.height) : 600
    const testFrame = document.createElement('iframe')
    testFrame.style.cssText = `position: fixed; left: 0; top: 0; z-index: -1; clip-path: inset(100%); width: ${vpWidth}px; height: ${vpHeight}px; opacity: 0; pointer-events: none;`
    document.body.appendChild(testFrame)

    // Listen for test results posted from the iframe
    const messageHandler = (event: MessageEvent) => {
      if (event.data?.type !== 'tosi-test-results') return
      // Only from OUR test frame: any other window on the page could otherwise post results.
      if (event.source !== testFrame.contentWindow) return
      const { filename, results } = event.data as {
        type: string
        filename: string
        results: TestResults
      }
      if (!pageTestResults[filename]) {
        pageTestResults[filename] = {
          passed: true,
          tests: [],
          totalPassed: 0,
          totalFailed: 0,
        }
      }
      const pageResults = pageTestResults[filename]
      pageResults.tests.push(...results.tests)
      pageResults.totalPassed += results.passed
      pageResults.totalFailed += results.failed
      pageResults.passed = pageResults.totalFailed === 0
      updateDocTestStatus(filename)
      updateTestWidget()
    }
    window.addEventListener('message', messageHandler)

    for (const doc of docsWithTests) {
      // Skip current page — it runs tests naturally
      if (doc.filename === currentFilename) continue

      // Navigate iframe to the page
      testFrame.src =
        routing === 'path'
          ? `${window.location.origin}${hrefFor(doc.filename)}?_testMode=1`
          : `${window.location.origin}${window.location.pathname}?${doc.filename}&_testMode=1`

      // Wait for the iframe to signal it's done. LONGER than the frame's own 30s stall
      // timeout, so a frame that gives up on a hung example gets to say so: at the same 30s
      // the two raced, and the parent could move on a moment before the stall report landed.
      let timedOut = false
      await new Promise<void>((resolve) => {
        const deadline = Date.now() + PAGE_DEADLINE_MS
        const onDone = (event: MessageEvent) => {
          if (
            event.source === testFrame.contentWindow &&
            event.data?.type === 'tosi-tests-done' &&
            event.data.filename === doc.filename
          ) {
            window.removeEventListener('message', onDone)
            resolve()
          }
        }
        window.addEventListener('message', onDone)
        setTimeout(() => {
          window.removeEventListener('message', onDone)
          timedOut = true
          resolve()
        }, deadline - Date.now())
      })

      // A page that timed out fails even if some of its examples had reported (closePage).
      pageTestResults[doc.filename] = closePage(pageTestResults[doc.filename], {
        timedOut,
        deadlineMs: PAGE_DEADLINE_MS,
      })
      updateDocTestStatus(doc.filename)
      markPageTested(doc.filename)
    }

    // Clean up
    window.removeEventListener('message', messageHandler)
    testFrame.remove()

    // The page the reader is on ran its tests in place; wait for them and close its entry.
    if (docsWithTests.some((d) => d.filename === currentFilename)) {
      void settleCurrentPage(currentFilename)
    }
  }

  // Run background tests when enabled (initially or when toggled on). Every page where a
  // developer is looking, the tunnel included; elsewhere only the page on screen.
  const startBackgroundTests = () => {
    if (!testManager.enabled.value) return
    if (servedForDevelopment()) {
      setTimeout(runBackgroundTests, 1000)
    } else {
      /*
      The page showing NOW, not the one this browser was constructed on. Tests are toggled
      from the menu, often after navigating, and the construction-time doc then named a page
      that is no longer on screen: it was judged by a census of some other page and recorded
      as "reported no tests" without ever having run (1.16.8 re-review, F1).
      */
      const showing = String(app.currentDoc.filename)
      const showingDoc = docs.find((d) => d.filename === showing)
      const currentHasTests = hasTestBlock(showingDoc?.text, examplePolicy())
      if (currentHasTests) {
        pagesWithTests = 1
        setTestWidgetRunning()
        // Not a flat timer: the same settle-and-close rule as on localhost.
        void settleCurrentPage(showing)
      } else if (testResultsResolve) {
        testResultsResolve({
          passed: 0,
          failed: 0,
          pages: {},
          pagesWithTests: 0,
          pagesTested: 0,
        })
        testResultsResolve = undefined
      }
    }
  }

  // Start now if enabled, and watch for toggle. A memory-routed (embedded)
  // browser never spawns the test-runner iframes (they'd navigate the host site).
  if (!memoryRouting) {
    startBackgroundTests()
    testManager.enabled.observe(startBackgroundTests)
  }

  /*
  The host assembles the Source submenu from these.

  It used to be a floating `<> Source` chip pinned over the content — a second, separate
  affordance for something the app menu was already the home of. Handing the ITEMS to the
  host instead means one menu, and the host decides placement; an empty array (a doc with
  no source file) tells it to omit the submenu rather than show an empty one.

  Same seam `navigate` uses below: a property on the returned element, because the host is
  a different component that already holds this reference.
  */
  ;(container as any).sourceMenuItems = sourceMenuItems
  ;(container as any).testsMenuItem = testsMenuItem

  /*
  On a narrow header (an iPhone held upright) the link icons crowd the title and the search, so
  below HEADER_LINKS_COLLAPSE_PX of header they hide and the settings menu lists them instead
  (the maintainer, 2026-10-21). Measured on the header, not the window, because a doc browser
  can be embedded in something narrower than the page.
  */
  const collapseLinks = () => {
    headerBar.classList.toggle(
      '-links-collapsed',
      headerBar.clientWidth > 0 &&
        headerBar.clientWidth < HEADER_LINKS_COLLAPSE_PX
    )
    updateSearchTrigger()
  }
  if (typeof ResizeObserver === 'function') {
    new ResizeObserver(collapseLinks).observe(headerBar)
  }
  collapseLinks()
  ;(container as any).headerLinkMenuItems = () =>
    headerBar.classList.contains('-links-collapsed')
      ? headerLinks.map((link) => ({
          caption: link.label,
          icon:
            link.icon && (icons as any)[link.icon] ? link.icon : 'externalLink',
          action: () => window.open(link.href, '_blank', 'noopener'),
        }))
      : []

  // Memory routing: let the host drive navigation programmatically (by slug) and
  // read the current slug back, so the browser can live in a floating panel etc.
  if (memoryRouting) {
    ;(container as any).navigate = (slug: string) =>
      navigateTo(filenameForSlug(slug) || slug)
    Object.defineProperty(container, 'currentSlug', {
      get: () => slugFor(String(app.currentDoc.filename)),
    })
  }

  return container as HTMLElement
}
